import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    CssBaseline,
    TextField,
    Typography,
    Card,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";

import logo from "../../Assets/Images/Logo 4.png";

const SUPER_ADMIN_TOKEN = "superadmin2024"; // Change this as needed

// Use either local or production API URL
const APIURL = window.location.hostname === 'localhost' 
    ? "http://localhost:5400/auth" 
    : "https://admin.apitrading.lk/auth";

export default function Signup() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [userToken, setUserToken] = useState("");
    const [error, setError] = useState("");
    const [canSignup, setCanSignup] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [successAlert, setSuccessAlert] = useState(false);
    const [loading, setLoading] = useState(false); // State for progress indicator
    const navigate = useNavigate();

    // Handle Super Admin Token Input
    const handleTokenInput = (event) => {
        setUserToken(event.target.value);
        setCanSignup(event.target.value === SUPER_ADMIN_TOKEN);
    };

    // Verify if email exists in the system
    const handleEmailVerification = async () => {
        try {
            const response = await fetch(`${APIURL}/verify-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            if (data.exists) {
                setEmailVerified(true);
                setError("");
            } else {
                setError("Email not found in system.");
            }
        } catch (err) {
            console.error("Email Verification Error:", err);
            setError("Server error. Try again later.");
        }
    };

    // Handle Signup (Update Password)
    const handleSignup = async (event) => {
        event.preventDefault();
        if (!canSignup || !emailVerified) {
            setError("Invalid token or email not verified.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        try {
            const response = await fetch(`${APIURL}/update-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();
            if (data.success) {
                setSuccessAlert(true); 
                setLoading(true); 
                setError("");

                // ✅ Navigate to login page after 5 seconds
                setTimeout(() => {
                    navigate("/");
                }, 3000);
            } else {
                setError("Failed to update password.");
            }
        } catch (err) {
            console.error("Password Update Error:", err);
            setError("Server error. Try again later.");
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                textAlign: "center",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                backdropFilter: "blur(5px)",
            }}
        >
            {/* Full-screen Title */}
            <Typography
                variant="h2"
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "3rem",
                    fontWeight: "bold",
                    color: "white",
                    textShadow: "2px 2px 10px rgba(0, 0, 0, 0.9)",
                    fontFamily: "Times New Roman, serif",
                    textAlign: "center",
                    width: "100%",
                    marginTop: "5%",
                }}
            >
                <span style={{ color: "#F15A24" }}>API Trading</span>
            </Typography>

            {/* Subtitle */}
            <Typography
                variant="h4"
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "white",
                    textAlign: "center",
                    width: "100%",
                    marginTop: "1%",
                }}
            >
                Super Admin Password Reset
            </Typography>

            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Card
                    sx={{
                        padding: 4,
                        borderRadius: 2,
                        backgroundColor: "rgba(112, 112, 112, 0.4)",
                        color: "white",
                        backdropFilter: "blur(15px)",
                        boxShadow: "0 8px 32px 0 rgba(135, 64, 31, 0.37)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                >
                    {/* ✅ Show Progress Indicator If Loading */}
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                            <CircularProgress sx={{ color: "#F15A24" }} />
                        </Box>
                    ) : (
                        <>
                            <Typography>Type Super Admin Token To Change Password</Typography>
                            <Box component="form" onSubmit={handleSignup} sx={{ mt: 2 }}>
                                <TextField margin="normal" fullWidth label="Super Admin Token" type="text" variant="outlined" sx={{
                                    input: { color: "#bbb" },
                                    label: { color: "#bbb" },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": { borderColor: "#bbb" },
                                        "&:hover fieldset": { borderColor: "white" },
                                        "&.Mui-focused fieldset": { borderColor: "#F15A24" },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#bbb",
                                        "&.Mui-focused": {
                                            color: "#F15A24",
                                        },
                                    },
                                }}
                                    value={userToken} onChange={handleTokenInput} disabled={canSignup}
                                />
                                {canSignup && !emailVerified && (
                                    <>
                                        <TextField margin="normal" fullWidth label="Registered Email" type="email" variant="outlined" sx={{
                                            input: { color: "#bbb" },
                                            label: { color: "#bbb" },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#bbb" },
                                                "&:hover fieldset": { borderColor: "white" },
                                                "&.Mui-focused fieldset": { borderColor: "#F15A24" },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "#bbb",
                                                "&.Mui-focused": {
                                                    color: "#F15A24",
                                                },
                                            },
                                        }}
                                            onChange={(e) => setEmail(e.target.value)} />
                                        <Button fullWidth variant="contained" sx={{ mt: 2, backgroundColor: "#F15A24" }}
                                            onClick={handleEmailVerification}>
                                            Verify Email
                                        </Button>
                                    </>
                                )}
                                {emailVerified && (
                                    <>
                                        <TextField margin="normal" fullWidth label="New Password" type="password" variant="outlined" sx={{
                                            input: { color: "#bbb" },
                                            label: { color: "#bbb" },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#bbb" },
                                                "&:hover fieldset": { borderColor: "white" },
                                                "&.Mui-focused fieldset": { borderColor: "#F15A24" },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "#bbb",
                                                "&.Mui-focused": {
                                                    color: "#F15A24",
                                                },
                                            },
                                        }}
                                            onChange={(e) => setPassword(e.target.value)} />
                                        <TextField margin="normal" fullWidth label="Re-type New Password" type="password" variant="outlined" sx={{
                                            input: { color: "#bbb" },
                                            label: { color: "#bbb" },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#bbb" },
                                                "&:hover fieldset": { borderColor: "white" },
                                                "&.Mui-focused fieldset": { borderColor: "#F15A24" },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "#bbb",
                                                "&.Mui-focused": {
                                                    color: "#F15A24",
                                                },
                                            },
                                        }}
                                            onChange={(e) => setConfirmPassword(e.target.value)} />
                                        <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, backgroundColor: "#F15A24" }}>
                                            Update Password
                                        </Button>
                                    </>
                                )}
                                {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
                            </Box>
                        </>
                    )}
                </Card>

                <Box component="img" src={logo} alt="Logo" sx={{ height: "60px", mt: 4 }} />
            </Container>

            {/* ✅ Success Snackbar Alert */}
            <Snackbar open={successAlert} autoHideDuration={3000} onClose={() => setSuccessAlert(false)}>
                <Alert onClose={() => setSuccessAlert(false)} severity="success" sx={{ width: "100%" }}>
                    Password updated successfully! Redirecting...
                </Alert>
            </Snackbar>
        </Box>
    );
}
